import { User, TransactionHistoryItem, TierProgressResponse } from '@lunit/shared-types';
import { api } from './client';
import { withTelegramId } from './session';

/**
 * Telegram user data for syncing with backend
 */
interface TelegramUserData {
  firstName?: string;
  lastName?: string;
  username?: string;
}

/**
 * Get or create current user
 * @param telegramId - Telegram user ID
 * @param silent - Don't show error toast
 * @param referralCode - Optional referral code (telegramId of referrer)
 * @param telegramUserData - Telegram user data for name sync
 */
export async function getCurrentUser(
  telegramId: number,
  silent = false,
  referralCode?: string,
  telegramUserData?: TelegramUserData
): Promise<User> {
  const params = new URLSearchParams();
  params.set('telegramId', telegramId.toString());

  if (referralCode) {
    params.set('referralCode', referralCode);
    console.log('[users.ts] Passing referralCode to API:', referralCode);
  }

  // Sync Telegram user name with backend on every login
  if (telegramUserData?.firstName) {
    params.set('firstName', telegramUserData.firstName);
  }
  if (telegramUserData?.lastName) {
    params.set('lastName', telegramUserData.lastName);
  }
  if (telegramUserData?.username) {
    params.set('username', telegramUserData.username);
  }

  const url = `/users/me?${params.toString()}`;

  // DIAGNOSTIC: Log what we're sending to backend
  console.log('[users.ts] 📤 Sending user data to API:', {
    url,
    telegramId,
    firstName: telegramUserData?.firstName || '(not provided)',
    lastName: telegramUserData?.lastName || '(not provided)',
    username: telegramUserData?.username || '(not provided)',
    hasFirstName: !!telegramUserData?.firstName,
    hasLastName: !!telegramUserData?.lastName,
    hasUsername: !!telegramUserData?.username
  });

  return api.get<User>(url, { silent });
}

// PWA (cookie-сессия): профиль и прогресс по uuid клиента — роуты за CustomerAuth.
export async function getUserById(userId: string, silent = false): Promise<User> {
  return api.get<User>(`/users/${userId}`, { silent });
}

export async function getTierProgressById(userId: string): Promise<TierProgressResponse> {
  return api.get<TierProgressResponse>(`/users/${userId}/tier-progress`);
}

export async function getUserTransactions(userId: string, venueId?: string): Promise<TransactionHistoryItem[]> {
  // Эндпоинт использует venueSlug из заголовков, но можно передать venueId как query параметр для совместимости
  const url = venueId
    ? `/users/${userId}/transactions?venueId=${venueId}`
    : `/users/${userId}/transactions`;
  return api.get<TransactionHistoryItem[]>(url);
}

export async function getTierProgress(telegramId: number): Promise<TierProgressResponse> {
  return api.get<TierProgressResponse>(`/users/me/tier-progress?telegramId=${telegramId}`);
}

/** Доказательство владения номером для PUT /users/me/phone (K-001). */
export interface PhoneProof {
  /** Номер: при контакте — сверка, при коде — номер, на который звонили. */
  phone?: string;
  /** event.response из Telegram requestContact — подписанный Telegram контакт. */
  contact?: string;
  /** Последние 4 цифры звонка после POST /phone-verification/initiate. */
  code?: string;
}

/** Профиль после привязки; merged — номер был у карточки PWA, её история перенесена в эту. */
export type SavePhoneResult = User & { merged?: boolean };

/**
 * Привязать телефон (K-001): подписанный контакт Telegram или код из звонка — и только своей карточке
 * (customer-сессия, Bearer из /auth/telegram). Если номер принадлежал карточке PWA этого заведения,
 * новый бэкенд переносит её историю сюда и отвечает merged: true (старый — 409 PHONE_TAKEN_BY_PWA).
 * `?telegramId=` — только сверка с сессией (api/session.ts), и только если он известен.
 * Тихо: текст ошибки выбирает экран (phoneErrorMessage).
 */
export async function saveUserPhone(telegramId: number | null | undefined, proof: PhoneProof): Promise<SavePhoneResult> {
  return api.put<SavePhoneResult>(withTelegramId('/users/me/phone', telegramId), proof, { silent: true });
}

// ============================================================================
// Invited Friends (My Friends)
// ============================================================================

export interface InvitedFriend {
  id: string;
  firstName: string | null;
  lastName: string | null;
  joinedAt: string;
  hasMadePurchase: boolean;
  bonusEarned: number;
  status: 'completed' | 'pending';
}

export interface InvitedByInfo {
  name: string | null;
  bonusReceived: number;
  status: 'received' | 'pending';
}

export interface MyFriendsResponse {
  friends: InvitedFriend[];
  totalFriends: number;
  totalEarned: number;
  invitedBy: InvitedByInfo | null;
}

/**
 * Get list of invited friends and referral info. Клиент — из сессии входа (K-002);
 * telegramId — необязательная сверка (только у входа Telegram).
 */
export async function getMyFriends(telegramId?: number | null): Promise<MyFriendsResponse> {
  return api.get<MyFriendsResponse>(withTelegramId('/users/me/invited-friends', telegramId));
}

// ============================================================================
// Redemption Gate Status
// ============================================================================

export interface GateConditionProgress {
  current: number;
  required: number;
  percent: number;
  met: boolean;
}

export interface RedemptionGateStatus {
  gateEnabled: boolean;
  allowed: boolean;
  reason?: string;
  progress?: {
    points?: GateConditionProgress;
    purchases?: GateConditionProgress;
  };
}

export async function getRedemptionGateStatus(): Promise<RedemptionGateStatus> {
  // Identity — из customer-сессии (Bearer mini-app / cookie PWA); query-параметры не доверяются (anti-IDOR).
  return api.get<RedemptionGateStatus>('/users/me/redemption-gate-status');
}
